import { isValid, parseJSON } from 'date-fns';
import { ref, watch, type Ref, type UnwrapRef } from 'vue';
import { useRouter } from 'vue-router';

interface Serializer<T> {
  serialize: (value: T) => string;
  deserialize: (value: string) => T;
}

const createJsonSerializer = <T>() => ({
  serialize(value: T) {
    return JSON.stringify(value);
  },
  deserialize(value: string): T {
    return JSON.parse(value, (key, jsonValue) => {
      if (typeof jsonValue !== 'string') return jsonValue;
      const parsedValue = parseJSON(jsonValue);
      if (isValid(parsedValue)) {
        return parsedValue;
      }
      return jsonValue;
    });
  },
});

// eslint-disable-next-line import/prefer-default-export
export function useQueryRef<T extends object>(
  name: string,
  value: T
): [T] extends [Ref] ? T : Ref<UnwrapRef<T>>;
export function useQueryRef<T extends object>(
  name: string,
  value: T,
  serializer: Serializer<T>
): [T] extends [Ref] ? T : Ref<UnwrapRef<T>>;
export function useQueryRef<T>(name: string, value: T): Ref<UnwrapRef<T>>;
export function useQueryRef<T = any>(name: string): Ref<T | undefined>;
export function useQueryRef<T>(name: string, initialValue?: any, serializer: Serializer<T> = createJsonSerializer<T>()) {
  const params = new URLSearchParams(window.location.search);
  const router = useRouter();
  const storedValue = params.get(name);
  let init = initialValue;

  if (storedValue) {
    const value = Array.isArray(storedValue) ? storedValue.join(', ') : storedValue;
    init = serializer.deserialize(decodeURIComponent(value)) as T | undefined;
  }

  const value = ref(init);

  watch(value, (newValue) => {
    const newParams = new URLSearchParams(window.location.search);
    if (!newValue) {
      newParams.delete(name);
    } else {
      newParams.set(name, encodeURIComponent(serializer.serialize(newValue)));
    }

    router.replace({
      query: Object.fromEntries(newParams.entries()),
    });
  });

  return value;
}
